
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { supabase } from '@/supabaseClient';
import { MetabolicService } from '@ai-services/metabolic.service';

export interface AthleteProfile {
    id: string;
    birth_date: string | null;
    gender: 'MALE' | 'FEMALE' | 'OTHER';
    height_cm: number;
    baseline_weight_kg?: number | null;
    body_fat_percentage?: number | null;
    activity_multiplier: number;
    target_calories_base: number | null;
    primary_goal: 'FAT_LOSS' | 'MUSCLE_GAIN' | 'RECOMPOSITION' | 'MAINTENANCE';
    insulin_sensitivity: 'HIGH' | 'NORMAL' | 'LOW';
}

interface AthleteState {
    profile: AthleteProfile | null;
    isLoading: boolean;
}

interface AthleteActions {
    fetchProfile: (userId: string) => Promise<void>;
    updateProfile: (userId: string, updates: Partial<AthleteProfile>) => Promise<void>;
    calculateTDEE: (currentWeightKg: number) => number;
}

/**
 * useAthleteStore (v4)
 * The biological heart of the metabolic engine.
 * HARDENED: Logic extracted to MetabolicService (SoC).
 */
export const useAthleteStore = create<AthleteState & AthleteActions>()(
    persist(
        (set, get) => ({
            profile: null,
            isLoading: false,

            fetchProfile: async (userId) => {
                if (!userId) return;
                set({ isLoading: true });
                try {
                    const { data, error } = await supabase
                        .schema('v3')
                        .from('athlete_profiles' as any)
                        .select('*')
                        .eq('id', userId)
                        .maybeSingle();
                    
                    if (!error && data) set({ profile: data as unknown as AthleteProfile });
                } finally {
                    set({ isLoading: false });
                }
            },

            updateProfile: async (userId, updates) => {
                if (!userId) return;
                set({ isLoading: true });
                
                try {
                    const { error } = await supabase
                        .schema('v3')
                        .from('athlete_profiles' as any)
                        .upsert({ ...updates, id: userId, updated_at: new Date().toISOString() } as any);

                    if (!error) {
                        const existing = get().profile || { 
                            id: userId, 
                            birth_date: null, 
                            gender: 'OTHER' as const, 
                            height_cm: 175, 
                            baseline_weight_kg: null,
                            body_fat_percentage: null,
                            activity_multiplier: 1.2, 
                            target_calories_base: null,
                            insulin_sensitivity: 'NORMAL' as const,
                            primary_goal: 'MAINTENANCE' as const
                        };
                        set({ profile: { ...existing, ...updates } as AthleteProfile });
                    }
                } finally {
                    set({ isLoading: false });
                }
            },

            calculateTDEE: (currentWeightKg) => {
                const p = get().profile;
                if (!p || !p.birth_date) return 2000;

                // DELEGATE TO SERVICE (SoC) - Support LBM
                const bmr = MetabolicService.calculateBMR(p.gender, p.height_cm, currentWeightKg, p.birth_date, p.body_fat_percentage);
                return Math.round(bmr * p.activity_multiplier);
            }
        }),
        { name: 'mp-athlete-profile' }
    )
);
